import { useState, useEffect } from 'react';
import { useIsClientNavigation } from './useIsClientNavigation';

/**
 * Хук для имитации загрузки данных с задержкой
 * SSR-совместимый:
 * - При SSR: возвращает переданные данные сразу без загрузки
 * - На клиенте при переходе с главной: показывает skeleton, затем загружает данные
 * - На клиенте при прямом заходе: показывает данные сразу без skeleton
 *
 * @param fetchData - функция для получения данных
 * @param delay - задержка в миллисекундах (по умолчанию 2000мс)
 * @returns объект с isLoading и data
 */
export const useFakeLoading = <T>(fetchData: () => T, delay: number = 2000): { isLoading: boolean; data: T | null } => {
    const isClientNavigation = useIsClientNavigation();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<T | null>(() => fetchData());

    useEffect(() => {
        // Обновляем данные при изменении fetchData
        const newData = fetchData();
        setData(newData);

        // Показываем skeleton только если это переход с SPA
        if (isClientNavigation) {
            setIsLoading(true);

            const timer = setTimeout(() => {
                setIsLoading(false);
            }, delay);

            return () => clearTimeout(timer);
        }
    }, [fetchData, delay, isClientNavigation]);

    return { isLoading, data };
};
